import type { SingleWitnessCoreferenceChainV2Figure } from '@variorum/character-index'
import type { CharacterEntity, CharacterOccurrence } from '../types/characterLayerTypes'
import { getCharacterFigureKind, getCharacterOccurrenceReferentIds } from './characterLayerHelpers'

export type CharacterIndexSyntheticFigureSource = 'character-index'

export interface CharacterIndexSyntheticFigureSourceRow {
  rowId: string
  rowLabel?: string
  witnessId: string
  text: string
}

export interface BuildCharacterIndexSyntheticFiguresArgs {
  characters: readonly CharacterEntity[]
  occurrences: readonly CharacterOccurrence[]
  sourceRows: readonly CharacterIndexSyntheticFigureSourceRow[]
}

type CoreferenceFigureKind = SingleWitnessCoreferenceChainV2Figure['kind']

const normalizeText = (value: unknown): string => (
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : ''
)

const getCharacterTransliteration = (character: CharacterEntity): string => {
  const metadata = character.metadata || {}
  return normalizeText(
    metadata.transliteration ||
    metadata.ijmes ||
    metadata.iJmes ||
    metadata.romanization ||
    ''
  )
}

const getCharacterKind = (character: CharacterEntity): CoreferenceFigureKind => {
  return getCharacterFigureKind(character) || 'person'
}

const getMentionFromOccurrence = (
  occurrence: CharacterOccurrence,
  character: CharacterEntity,
  sourceText: string
): string => {
  const label = normalizeText(occurrence.label)
  if (label) {
    return label
  }
  const { localStart, localEnd } = occurrence.locator
  if (
    typeof localStart === 'number' &&
    typeof localEnd === 'number' &&
    localStart >= 0 &&
    localEnd > localStart &&
    localEnd <= sourceText.length
  ) {
    const selected = normalizeText(sourceText.slice(localStart, localEnd))
    if (selected) {
      return selected
    }
  }
  return normalizeText(character.display_name) || character.character_id
}

const buildCandidateSurfaceForms = (character: CharacterEntity): string[] => (
  Array.from(new Set([
    character.display_name,
    ...(character.aliases || []),
  ].map(normalizeText).filter(Boolean)))
)

export const buildCharacterIndexSyntheticFigures = ({
  characters,
  occurrences,
  sourceRows,
}: BuildCharacterIndexSyntheticFiguresArgs): SingleWitnessCoreferenceChainV2Figure[] => {
  const sourceRowsByKey = new Map(
    sourceRows
      .filter(row => row.text.trim())
      .map(row => [`${row.witnessId}:${row.rowId}`, row])
  )
  const rowOrderByKey = new Map(
    sourceRows.map((row, index) => [`${row.witnessId}:${row.rowId}`, index])
  )
  const occurrencesByCharacterId = new Map<string, CharacterOccurrence[]>()
  occurrences.forEach(occurrence => {
    const rowKey = `${occurrence.locator.witnessId}:${occurrence.locator.rowId}`
    if (!sourceRowsByKey.has(rowKey)) {
      return
    }
    getCharacterOccurrenceReferentIds(occurrence).forEach(characterId => {
      const bucket = occurrencesByCharacterId.get(characterId) || []
      bucket.push(occurrence)
      occurrencesByCharacterId.set(characterId, bucket)
    })
  })

  return characters.flatMap(character => {
    const anchorName = normalizeText(character.display_name)
    if (!anchorName) {
      return []
    }

    const existingOccurrences = [...(occurrencesByCharacterId.get(character.character_id) || [])]
      .sort((left, right) => {
        const leftKey = `${left.locator.witnessId}:${left.locator.rowId}`
        const rightKey = `${right.locator.witnessId}:${right.locator.rowId}`
        const rowDelta = (rowOrderByKey.get(leftKey) ?? Number.MAX_SAFE_INTEGER) -
          (rowOrderByKey.get(rightKey) ?? Number.MAX_SAFE_INTEGER)
        if (rowDelta !== 0) {
          return rowDelta
        }
        return (left.locator.localStart ?? 0) - (right.locator.localStart ?? 0)
      })

    const chain = existingOccurrences.map((occurrence, index) => {
      const row = sourceRowsByKey.get(`${occurrence.locator.witnessId}:${occurrence.locator.rowId}`)
      const context = normalizeText(row?.text || occurrence.summary || '')
      return {
        id: `${character.character_id}.m${index + 1}`,
        mention: getMentionFromOccurrence(occurrence, character, row?.text || ''),
        context,
      }
    }).filter(item => item.context)

    if (chain.length === 0) {
      const forms = buildCandidateSurfaceForms(character)
      sourceRows.forEach(row => {
        const form = forms.find(candidate => row.text.includes(candidate))
        if (!form) {
          return
        }
        chain.push({
          id: `${character.character_id}.m${chain.length + 1}`,
          mention: form,
          context: normalizeText(row.text),
        })
      })
    }

    if (chain.length === 0) {
      return []
    }

    return [{
      id: character.character_id,
      kind: getCharacterKind(character),
      anchor: {
        name: anchorName,
        transliteration: getCharacterTransliteration(character),
      },
      chain,
    }]
  })
}
